import React, {
    useEffect,
    useState
} from "react";

import {
    useSearchParams
} from "react-router-dom";

import pb
from "../../lib/pb";


/*
==================================================
API
==================================================
*/

const API_URL =
    "https://api.myfavv.live";


/*
==================================================
BRAND ORDERS
==================================================
*/

export default function BrandOrders({
    brand
}) {

    const [
        searchParams
    ] =
        useSearchParams();


    /*
    ==================================================
    CURRENT PAYMENT RETURN ORDER
    ==================================================
    */

    const orderId =
        searchParams.get(
            "order"
        );


    const paymentMode =
        searchParams.get(
            "payment"
        );


    /*
    ==================================================
    PAYMENT STATE
    ==================================================
    */

    const [
        status,
        setStatus
    ] =
        useState(
            paymentMode === "return" && orderId
                ? "processing"
                : "idle"
        );


    const [
        order,
        setOrder
    ] =
        useState(
            null
        );


    const [
        error,
        setError
    ] =
        useState(
            ""
        );


    const [
        attempts,
        setAttempts
    ] =
        useState(
            0
        );


    /*
    ==================================================
    BRAND ORDERS STATE
    ==================================================
    */

    const [
        orders,
        setOrders
    ] =
        useState(
            []
        );


    const [
        ordersLoading,
        setOrdersLoading
    ] =
        useState(
            true
        );


    const [
        ordersError,
        setOrdersError
    ] =
        useState(
            ""
        );


    /*
    ==================================================
    LOAD CURRENT ORDER
    ==================================================
    */

    async function loadOrder(
        id
    ) {

        if (!id) {

            return null;

        }


        try {

            const record =
                await pb
                    .collection(
                        "orders"
                    )
                    .getOne(
                        id,
                        {
                            requestKey:
                                null
                        }
                    );


            setOrder(
                record
            );


            return record;

        }

        catch (err) {

            console.error(
                "Unable to load order:",
                err
            );


            return null;

        }

    }


    /*
    ==================================================
    LOAD ALL ORDERS FOR CURRENT BRAND
    ==================================================
    */

    async function loadBrandOrders() {

        if (!brand?.id) {

            setOrders(
                []
            );

            setOrdersLoading(
                false
            );

            return;

        }


        try {

            setOrdersLoading(
                true
            );


            setOrdersError(
                ""
            );


            const records =
                await pb
                    .collection(
                        "orders"
                    )
                    .getFullList({
                        sort:
                            "-created",

                        filter:
                            `brand="${brand.id}"`,

                        requestKey:
                            null
                    });


            setOrders(
                records
            );

        }

        catch (err) {

            console.error(
                "Unable to load brand orders:",
                err
            );


            setOrdersError(
                "Unable to load orders."
            );

        }

        finally {

            setOrdersLoading(
                false
            );

        }

    }


    /*
    ==================================================
    VERIFY PAYMENT
    ==================================================
    */

    async function verifyPayment() {

        if (!orderId) {

            setStatus(
                "error"
            );


            setError(
                "Order ID is missing."
            );


            return;

        }


        setStatus(
            "processing"
        );


        setError(
            ""
        );


        try {

            /*
            ------------------------------------------
            Ask OUR backend.

            The browser never tells us whether the
            payment succeeded.
            ------------------------------------------
            */

            const response =
                await fetch(
                    `${API_URL}/payments/verify`,
                    {

                        method:
                            "POST",

                        headers: {

                            "Content-Type":
                                "application/json"

                        },

                        body:
                            JSON.stringify({

                                orderId

                            })

                    }
                );


            const data =
                await response
                    .json();


            /*
            ------------------------------------------
            PAYMENT VERIFICATION LOG

            Only log the safe payment status.
            ------------------------------------------
            */

            console.log(
                "Payment verification status:",
                data?.paymentStatus
            );


            /*
            ------------------------------------------
            VERIFIED
            ------------------------------------------

            /payments/verify returns:

            {
                success: true,
                verified: true,
                paymentStatus: "paid"
            }

            ------------------------------------------
            */

            if (
                response.ok &&
                data?.verified === true &&
                data?.paymentStatus === "paid"
            ) {

                await loadOrder(
                    orderId
                );


                /*
                --------------------------------------
                Refresh the brand order list as well.
                Inventory processing / confirmation
                may have happened server-side.
                --------------------------------------
                */

                await loadBrandOrders();


                setStatus(
                    "success"
                );


                return;

            }


            /*
            ------------------------------------------
            PAYMENT FAILED
            ------------------------------------------
            */

            if (
                data?.paymentStatus ===
                "failed"
            ) {

                await loadOrder(
                    orderId
                );


                await loadBrandOrders();


                setStatus(
                    "failed"
                );


                return;

            }


            /*
            ------------------------------------------
            PAYMENT STILL PROCESSING
            ------------------------------------------
            */

            if (
                data?.paymentStatus ===
                "pending"
            ) {

                setStatus(
                    "processing"
                );


                return;

            }


            /*
            ------------------------------------------
            VERIFICATION ERROR
            ------------------------------------------
            */

            setStatus(
                "error"
            );


            setError(
                data?.error ||
                "Payment could not be verified."
            );

        }

        catch (err) {

            console.error(
                "Payment verification request failed:",
                err
            );


            setStatus(
                "error"
            );


            setError(
                "Payment could not be verified."
            );

        }

    }


    /*
    ==================================================
    INITIAL PAYMENT RETURN LOAD
    ==================================================
    */

    useEffect(() => {

        let cancelled =
            false;


        async function start() {

            /*
            ------------------------------------------
            Normal Brand Orders page

            No payment-return order exists.
            ------------------------------------------
            */

            if (
                paymentMode !== "return" ||
                !orderId
            ) {

                return;

            }


            /*
            ------------------------------------------
            Load local PocketBase order first.
            ------------------------------------------
            */

            await loadOrder(
                orderId
            );


            if (
                cancelled
            ) {

                return;

            }


            /*
            ------------------------------------------
            Verify against Cashfree.
            ------------------------------------------
            */

            await verifyPayment();

        }


        start();


        return () => {

            cancelled =
                true;

        };

    }, [
        orderId,
        paymentMode
    ]);


    /*
    ==================================================
    LOAD BRAND ORDERS
    ==================================================
    */

    useEffect(() => {

        loadBrandOrders();

    }, [
        brand?.id
    ]);


    /*
    ==================================================
    REALTIME BRAND ORDERS

    Whenever an order belonging to this brand changes,
    reload the brand order list.

    This covers:
    new order
    payment update
    inventory update
    confirmation
    fulfillment update
    other order changes
    ==================================================
    */

    useEffect(() => {

        if (!brand?.id) {

            return;

        }


        const handleOrderChange =
            async (
                event
            ) => {

                const changedOrder =
                    event?.record;


                /*
                --------------------------------------
                Only react to orders belonging to
                the currently displayed brand.
                --------------------------------------
                */

                if (
                    changedOrder?.brand !==
                    brand.id
                ) {

                    return;

                }


                await loadBrandOrders();


                /*
                --------------------------------------
                If this is the payment-return order,
                refresh that individual record too.
                --------------------------------------
                */

                if (
                    orderId &&
                    changedOrder?.id ===
                    orderId
                ) {

                    await loadOrder(
                        orderId
                    );

                }

            };


        pb.collection(
            "orders"
        ).subscribe(
            "*",
            handleOrderChange
        );


        return () => {

            try {

                pb.collection(
                    "orders"
                ).unsubscribe(
                    "*"
                );

            }

            catch {}

        };

    }, [
        brand?.id,
        orderId
    ]);


    /*
    ==================================================
    POLL PENDING PAYMENT

    If Cashfree is still processing, retry a few
    times rather than immediately showing failure.
    ==================================================
    */

    useEffect(() => {

        if (
            status !==
            "processing"
        ) {

            return;

        }


        if (
            attempts >= 5
        ) {

            return;

        }


        const timer =
            setTimeout(
                () => {

                    setAttempts(
                        previous =>
                            previous + 1
                    );


                    verifyPayment();

                },
                3000
            );


        return () => {

            clearTimeout(
                timer
            );

        };

    }, [
        status,
        attempts,
        orderId
    ]);


    /*
    ==================================================
    PAYMENT RETURN PROCESSING
    ==================================================
    */

    if (
        paymentMode === "return" &&
        orderId &&
        status === "processing"
    ) {

        return (

            <div
                style={{
                    minHeight:
                        "70vh",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    padding:
                        30,

                    background:
                        "#f7f7f7"
                }}
            >

                <div
                    style={{
                        background:
                            "#fff",

                        borderRadius:
                            18,

                        padding:
                            45,

                        maxWidth:
                            520,

                        width:
                            "100%",

                        textAlign:
                            "center",

                        boxShadow:
                            "0 10px 35px rgba(0,0,0,0.08)"
                    }}
                >

                    <div
                        style={{
                            width:
                                42,

                            height:
                                42,

                            margin:
                                "0 auto 22px",

                            border:
                                "4px solid #e5e5e5",

                            borderTopColor:
                                "#111",

                            borderRadius:
                                "50%",

                            animation:
                                "myfavv-payment-spin 0.9s linear infinite"
                        }}
                    />

                    <h2
                        style={{
                            marginBottom:
                                10
                        }}
                    >
                        Payment processing...
                    </h2>

                    <p
                        style={{
                            color:
                                "#666",

                            margin:
                                0
                        }}
                    >
                        Please wait while we verify your
                        payment securely.
                    </p>

                    <p
                        style={{
                            color:
                                "#999",

                            fontSize:
                                13,

                            marginTop:
                                18
                        }}
                    >
                        Order #{orderId}
                    </p>

                    <style>
                        {`
                            @keyframes myfavv-payment-spin {
                                to {
                                    transform: rotate(360deg);
                                }
                            }
                        `}
                    </style>

                </div>

            </div>

        );

    }


    /*
    ==================================================
    PAYMENT SUCCESS
    ==================================================
    */

    if (
        paymentMode === "return" &&
        orderId &&
        status === "success"
    ) {

        return (

            <div
                style={{
                    minHeight:
                        "70vh",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    padding:
                        30,

                    background:
                        "#f7f7f7"
                }}
            >

                <div
                    style={{
                        background:
                            "#fff",

                        borderRadius:
                            18,

                        padding:
                            45,

                        maxWidth:
                            560,

                        width:
                            "100%",

                        textAlign:
                            "center",

                        boxShadow:
                            "0 10px 35px rgba(0,0,0,0.08)"
                    }}
                >

                    <div
                        style={{
                            width:
                                64,

                            height:
                                64,

                            margin:
                                "0 auto 22px",

                            borderRadius:
                                "50%",

                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center",

                            background:
                                "#eaf8ef",

                            fontSize:
                                32
                        }}
                    >
                        ✓
                    </div>


                    <h1
                        style={{
                            marginBottom:
                                10
                        }}
                    >
                        Payment successful
                    </h1>


                    <p
                        style={{
                            color:
                                "#666",

                            marginBottom:
                                20
                        }}
                    >
                        Your payment has been verified
                        successfully.
                    </p>


                    <div
                        style={{
                            background:
                                "#f7f7f7",

                            borderRadius:
                                12,

                            padding:
                                18,

                            marginTop:
                                20
                        }}
                    >

                        <strong>
                            Order #{order?.id || orderId}
                        </strong>

                    </div>

                </div>

            </div>

        );

    }


    /*
    ==================================================
    PAYMENT FAILED
    ==================================================
    */

    if (
        paymentMode === "return" &&
        orderId &&
        status === "failed"
    ) {

        return (

            <div
                style={{
                    minHeight:
                        "70vh",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    padding:
                        30,

                    background:
                        "#f7f7f7"
                }}
            >

                <div
                    style={{
                        background:
                            "#fff",

                        borderRadius:
                            18,

                        padding:
                            45,

                        maxWidth:
                            560,

                        width:
                            "100%",

                        textAlign:
                            "center",

                        boxShadow:
                            "0 10px 35px rgba(0,0,0,0.08)"
                    }}
                >

                    <div
                        style={{
                            width:
                                64,

                            height:
                                64,

                            margin:
                                "0 auto 22px",

                            borderRadius:
                                "50%",

                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center",

                            background:
                                "#fff0f0",

                            fontSize:
                                30
                        }}
                    >
                        ×
                    </div>


                    <h1>
                        Payment could not be verified
                    </h1>


                    <p
                        style={{
                            color:
                                "#666"
                        }}
                    >
                        The payment was not completed
                        successfully.
                    </p>


                    <p
                        style={{
                            color:
                                "#999",

                            fontSize:
                                13,

                            marginTop:
                                18
                        }}
                    >
                        Order #{orderId}
                    </p>

                </div>

            </div>

        );

    }


    /*
    ==================================================
    PAYMENT VERIFICATION ERROR
    ==================================================
    */

    if (
        paymentMode === "return" &&
        orderId &&
        status === "error"
    ) {

        return (

            <div
                style={{
                    minHeight:
                        "70vh",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center",

                    padding:
                        30,

                    background:
                        "#f7f7f7"
                }}
            >

                <div
                    style={{
                        background:
                            "#fff",

                        borderRadius:
                            18,

                        padding:
                            45,

                        maxWidth:
                            560,

                        width:
                            "100%",

                        textAlign:
                            "center",

                        boxShadow:
                            "0 10px 35px rgba(0,0,0,0.08)"
                    }}
                >

                    <h1>
                        Payment could not be verified
                    </h1>


                    <p
                        style={{
                            color:
                                "#666"
                        }}
                    >
                        {error ||
                            "We could not securely verify this payment."}
                    </p>


                    <p
                        style={{
                            color:
                                "#999",

                            fontSize:
                                13,

                            marginTop:
                                18
                        }}
                    >
                        Order #{orderId}
                    </p>

                </div>

            </div>

        );

    }


    /*
    ==================================================
    BRAND ORDERS PAGE
    ==================================================
    */

    return (

        <div
            style={{
                padding:
                    30,

                maxWidth:
                    1400,

                margin:
                    "0 auto"
            }}
        >

            <div
                style={{
                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "space-between",

                    gap:
                        20,

                    marginBottom:
                        28
                }}
            >

                <div>

                    <h1
                        style={{
                            margin:
                                0,

                            marginBottom:
                                8
                        }}
                    >
                        Orders
                    </h1>

                    <p
                        style={{
                            margin:
                                0,

                            color:
                                "#666"
                        }}
                    >
                        Orders placed for{" "}
                        <strong>
                            {brand?.name ||
                                brand?.title ||
                                "your brand"}
                        </strong>
                    </p>

                </div>


                <div
                    style={{
                        background:
                            "#f5f5f5",

                        borderRadius:
                            10,

                        padding:
                            "10px 16px",

                        fontSize:
                            14,

                        color:
                            "#555"
                    }}
                >
                    {orders.length}{" "}
                    {orders.length === 1
                        ? "order"
                        : "orders"}
                </div>

            </div>


            /*
            ==================================================
            LOADING
            ==================================================
            */

            {ordersLoading && (

                <div
                    style={{
                        background:
                            "#fff",

                        borderRadius:
                            16,

                        padding:
                            40,

                        textAlign:
                            "center",

                        boxShadow:
                            "0 5px 25px rgba(0,0,0,0.06)"
                    }}
                >
                    Loading orders...
                </div>

            )}


            /*
            ==================================================
            ERROR
            ==================================================
            */

            {!ordersLoading &&
                ordersError && (

                    <div
                        style={{
                            background:
                                "#fff0f0",

                            color:
                                "#b00020",

                            borderRadius:
                                14,

                            padding:
                                20,

                            marginBottom:
                                20
                        }}
                    >
                        {ordersError}
                    </div>

                )}


            /*
            ==================================================
            EMPTY
            ==================================================
            */

            {!ordersLoading &&
                !ordersError &&
                orders.length === 0 && (

                    <div
                        style={{
                            background:
                                "#fff",

                            borderRadius:
                                16,

                            padding:
                                50,

                            textAlign:
                                "center",

                            boxShadow:
                                "0 5px 25px rgba(0,0,0,0.06)"
                        }}
                    >

                        <h2
                            style={{
                                marginTop:
                                    0
                            }}
                        >
                            No orders yet
                        </h2>

                        <p
                            style={{
                                color:
                                    "#666",

                                margin:
                                    0
                            }}
                        >
                            Orders placed through your
                            marketplace will appear here.
                        </p>

                    </div>

                )}


            /*
            ==================================================
            ORDERS
            ==================================================
            */

            {!ordersLoading &&
                orders.length > 0 && (

                    <div
                        style={{
                            display:
                                "grid",

                            gap:
                                18
                        }}
                    >

                        {orders.map(
                            currentOrder => (

                                <OrderCard
                                    key={
                                        currentOrder.id
                                    }

                                    order={
                                        currentOrder
                                    }
                                />

                            )
                        )}

                    </div>

                )}

        </div>

    );

}


/*
==================================================
ORDER CARD
==================================================
*/

function OrderCard({
    order
}) {

    const items =
        Array.isArray(
            order.items
        )
            ?
            order.items
            :
            [];


    return (

        <div
            style={{
                background:
                    "#fff",

                borderRadius:
                    16,

                padding:
                    24,

                boxShadow:
                    "0 5px 25px rgba(0,0,0,0.06)"
            }}
        >

            <div
                style={{
                    display:
                        "flex",

                    justifyContent:
                        "space-between",

                    alignItems:
                        "flex-start",

                    gap:
                        20,

                    flexWrap:
                        "wrap",

                    marginBottom:
                        20
                }}
            >

                <div>

                    <div
                        style={{
                            fontWeight:
                                700,

                            fontSize:
                                17,

                            marginBottom:
                                6
                        }}
                    >
                        #{order.id}
                    </div>

                    <div
                        style={{
                            color:
                                "#777",

                            fontSize:
                                13
                        }}
                    >
                        {formatDate(
                            order.created
                        )}
                    </div>

                </div>


                <div
                    style={{
                        display:
                            "flex",

                        gap:
                            8,

                        flexWrap:
                            "wrap"
                    }}
                >

                    <StatusBadge
                        label="Payment"
                        value={
                            order.paymentStatus
                        }
                    />

                    <StatusBadge
                        label="Order"
                        value={
                            order.status
                        }
                    />

                    <StatusBadge
                        label="Fulfillment"
                        value={
                            order.fulfillmentStatus
                        }
                    />

                </div>

            </div>


            <div
                style={{
                    display:
                        "grid",

                    gridTemplateColumns:
                        "minmax(0, 1fr) auto",

                    gap:
                        24,

                    alignItems:
                        "start"
                }}
            >

                <div>

                    <div
                        style={{
                            fontWeight:
                                600,

                            marginBottom:
                                12
                        }}
                    >
                        {order.customerName ||
                            "Customer"}
                    </div>


                    <div
                        style={{
                            color:
                                "#777",

                            fontSize:
                                13,

                            marginBottom:
                                16
                        }}
                    >
                        {order.customerEmail ||
                            ""}

                        {order.customerPhone
                            ? ` • ${order.customerPhone}`
                            : ""}
                    </div>


                    <div>

                        {items.map(
                            (
                                item,
                                index
                            ) => (

                                <div
                                    key={
                                        `${order.id}-${index}`
                                    }

                                    style={{
                                        display:
                                            "flex",

                                        justifyContent:
                                            "space-between",

                                        gap:
                                            20,

                                        padding:
                                            "10px 0",

                                        borderBottom:
                                            "1px solid #eee"
                                    }}
                                >

                                    <div>

                                        <div
                                            style={{
                                                fontWeight:
                                                    600
                                            }}
                                        >
                                            {item.title ||
                                                item.name ||
                                                item.productId ||
                                                "Product"}
                                        </div>

                                        <div
                                            style={{
                                                color:
                                                    "#777",

                                                fontSize:
                                                    13,

                                                marginTop:
                                                    4
                                            }}
                                        >
                                            {item.quantity} ×{" "}
                                            ₹
                                            {formatMoney(
                                                item.unitPrice
                                            )}
                                        </div>

                                    </div>


                                    <div
                                        style={{
                                            fontWeight:
                                                600,

                                            whiteSpace:
                                                "nowrap"
                                        }}
                                    >
                                        ₹
                                        {formatMoney(
                                            item.subtotal
                                        )}
                                    </div>

                                </div>

                            )
                        )}

                    </div>

                </div>


                <div
                    style={{
                        minWidth:
                            150,

                        textAlign:
                            "right"
                    }}
                >

                    <div
                        style={{
                            color:
                                "#777",

                            fontSize:
                                13,

                            marginBottom:
                                5
                        }}
                    >
                        Total
                    </div>


                    <div
                        style={{
                            fontSize:
                                24,

                            fontWeight:
                                700
                        }}
                    >
                        ₹
                        {formatMoney(
                            order.grandTotal
                        )}
                    </div>

                </div>

            </div>


            <div
                style={{
                    display:
                        "flex",

                    justifyContent:
                        "space-between",

                    gap:
                        20,

                    flexWrap:
                        "wrap",

                    marginTop:
                        20,

                    paddingTop:
                        18,

                    borderTop:
                        "1px solid #eee",

                    color:
                        "#777",

                    fontSize:
                        13
                }}
            >

                <div>

                    <strong>
                        Inventory:
                    </strong>{" "}

                    {order.inventoryStatus ||
                        (
                            order.inventoryProcessed
                                ? "deducted"
                                : "pending"
                        )}

                </div>


                <div>

                    <strong>
                        Fulfillment:
                    </strong>{" "}

                    {order.fulfillmentStatus ||
                        "unfulfilled"}

                </div>

            </div>

        </div>

    );

}


/*
==================================================
STATUS BADGE
==================================================
*/

function StatusBadge({
    label,
    value
}) {

    const normalized =
        String(
            value ||
                "unknown"
        ).toLowerCase();


    let background =
        "#f3f3f3";


    let color =
        "#555";


    if (
        normalized ===
            "paid" ||

        normalized ===
            "confirmed" ||

        normalized ===
            "deducted"
    ) {

        background =
            "#eaf8ef";

        color =
            "#176b38";

    }

    else if (
        normalized ===
            "pending" ||

        normalized ===
            "payment_pending" ||

        normalized ===
            "unfulfilled" ||

        normalized ===
            "inventory_pending"
    ) {

        background =
            "#fff7df";

        color =
            "#8a6500";

    }

    else if (
        normalized ===
            "failed" ||

        normalized ===
            "payment_failed" ||

        normalized ===
            "cancelled" ||

        normalized ===
            "canceled"
    ) {

        background =
            "#fff0f0";

        color =
            "#a30000";

    }


    return (

        <div
            style={{
                background,
                color,

                borderRadius:
                    999,

                padding:
                    "6px 10px",

                fontSize:
                    12,

                fontWeight:
                    600
            }}
        >
            {label}:{" "}
            {formatStatus(
                value
            )}
        </div>

    );

}


/*
==================================================
FORMAT STATUS
==================================================
*/

function formatStatus(
    value
) {

    if (!value) {

        return "Unknown";

    }


    return String(
        value
    )
        .replace(
            /_/g,
            " "
        )
        .replace(
            /\b\w/g,
            character =>
                character.toUpperCase()
        );

}


/*
==================================================
FORMAT DATE
==================================================
*/

function formatDate(
    value
) {

    if (!value) {

        return "—";

    }


    try {

        return new Date(
            value
        ).toLocaleString(
            "en-IN",
            {
                dateStyle:
                    "medium",

                timeStyle:
                    "short"
            }
        );

    }

    catch {

        return value;

    }

}


/*
==================================================
FORMAT MONEY
==================================================
*/

function formatMoney(
    value
) {

    const number =
        Number(
            value || 0
        );


    return number.toLocaleString(
        "en-IN",
        {
            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2
        }
    );

}